import { defaultFilter } from 'cmdk';

export interface RankableCommand {
  label: string;
  to: string;
  keywords?: readonly string[];
}

export interface RankableCommandGroup<T extends RankableCommand = RankableCommand> {
  label: string;
  items: readonly T[];
}

/**
 * Score a command against the palette query using cmdk's default filter
 * (command-score). Higher is better; 0 means no match.
 */
export function scoreCommandMatch(
  label: string,
  query: string,
  keywords: readonly string[] = [],
): number {
  const q = query.trim();
  if (!q) return 1;
  return defaultFilter(label, q, [...keywords]);
}

/**
 * Filter and sort command groups by cmdk match score so the best label hit
 * leads the list. Empty / whitespace queries keep the original IA order.
 */
export function rankCommandGroups<T extends RankableCommand>(
  groups: readonly RankableCommandGroup<T>[],
  query: string,
): RankableCommandGroup<T>[] {
  const q = query.trim();
  if (!q) return groups.map((group) => ({ label: group.label, items: [...group.items] }));

  return groups
    .map((group) => {
      const items = group.items
        .map((item) => ({
          item,
          score: scoreCommandMatch(item.label, q, item.keywords ?? []),
        }))
        .filter((entry) => entry.score > 0)
        .sort((a, b) => b.score - a.score || a.item.label.localeCompare(b.item.label))
        .map((entry) => entry.item);
      const best = items.reduce(
        (max, item) => Math.max(max, scoreCommandMatch(item.label, q, item.keywords ?? [])),
        0,
      );
      return { label: group.label, items, best };
    })
    .filter((group) => group.items.length > 0)
    .sort((a, b) => b.best - a.best || a.label.localeCompare(b.label))
    .map(({ label, items }) => ({ label, items }));
}
